<template>
  <div>
    <div ref="graphContainer" class="w-full h-full">
      <div :id="`container-${chart.Id}`" class="svg-container"></div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
// https://observablehq.com/@d3/chord-diagram
import * as d3 from "d3";

// #region Props, Emits
const props = defineProps({
  chart: {
    type: Object,
    required: true,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const defaultValue = 0.2; // 10%
const graphContainer = ref(null);
// #endregion

// #region Functions
function groupTicks(d, step) {
  const k = (d.endAngle - d.startAngle) / d.value;

  return d3.range(0, d.value, step).map((value) => {
    return { value: value, angle: value * k + d.startAngle };
  });
}

function formatValue(value, format = "percentage") {
  // Format as percentage
  if (format == "percentage") {
    return d3.format(".1~%")(value);
  } else if (format == "number") {
    return d3.format(",.0f")(value);
  }
}

function loadGraph(id, width, height) {
  const sankeyData = props.chart.propiedades.sankey;

  const sankeyNames = [];
  const sankeyColors = [];

  sankeyData.forEach((data) => {
    sankeyNames.push(data.nombre);
    sankeyColors.push(data.color);
  });

  const data = Object.assign(
    [
      // Apple - Apple | Apple - HTC | Apple - Huawei
      [0, defaultValue, 0, 0],

      // HTC - Apple | HTC - HTC | HTC - Huawei
      [0, 0, defaultValue, 0],

      // Huawei - Apple | Huawei - HTC | Huawei - Huawei
      [defaultValue, 0, 0, 0],
      [defaultValue, 0, 0, defaultValue],
    ],
    {
      names: sankeyNames,
      colors: sankeyColors,
    }
  );

  const { names, colors } = data;
  const outerRadius = Math.min(width, height) * 0.5 - 60;
  const innerRadius = outerRadius - 10;
  const tickStep = d3.tickStep(0, d3.sum(data.flat()), 100);

  const container = d3.select(id);

  const chord = d3
    .chord()
    .padAngle(10 / innerRadius) // separación de los nodos
    .sortSubgroups(d3.descending)
    .sortChords(d3.descending);

  const color = d3.scaleOrdinal(names, colors);

  const svg = container
    .append("svg")
    .attr("width", width)
    .attr("height", height)
    .attr("viewBox", [-width / 2, -height / 2, width, height])
    .attr("style", "width: 100%; height: auto; font: 10px sans-serif;");

  const chords = chord(data);

  const group = svg.append("g").selectAll().data(chords.groups).join("g");

  // ----------------- Corona de los grupos -----------------
  const arc = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);

  group
    .append("path")
    .attr("fill", (d) => color(names[d.index]))
    .attr("d", arc)
    .append("title")
    .text((d) => `${names[d.index]}\n${formatValue(d.value)}`);

  // ----------------- Ticks -----------------
  // ----------------- Posicionamiento de los ticks -----------------
  let angles = [];
  let finalAngles = [];
  let iteration = 0;

  const groupTick = group
    .append("g")
    .selectAll()
    .data((d) => {
      angles.push(d);

      const ticks = groupTicks(d, tickStep);

      return [ticks[0]];
    })
    .join("g")
    // hide ticks every 5 items
    // .attr("display", (d, i) => (i % 5 === 0 ? null : "none"))
    .attr("transform", () => {
      const startAngle = angles[iteration].startAngle;
      const endAngle = angles[iteration].endAngle;

      iteration++;

      let angle = (startAngle + endAngle) / 2;
      angle = (angle * 180) / Math.PI - 90;

      finalAngles.push(angle);

      return `rotate(${angle}) translate(${outerRadius + 15},0)`;
    });

  // ----------------- Textos de los ticks -----------------
  iteration = 0;

  groupTick
    .append("text")
    // .attr("x", 8)
    // .attr("dy", "0.35em")
    .attr("transform", () => {
      let rotationAngle = finalAngles[iteration];

      iteration++;

      return `rotate(${rotationAngle * -1})`;
    })
    .attr("text-anchor", (d) => (d.angle > Math.PI ? "end" : "start"));

  // ----------------- Lineas que marcan los porcentajes -----------------
  // groupTick.append("line").attr("stroke", "currentColor").attr("x2", 6);

  // ----------------- Links -----------------
  const ribbon = d3
    .ribbon()
    .radius(innerRadius - 1)
    .padAngle(1 / innerRadius);

  const informations = [];

  svg
    .append("g")
    .attr("fill-opacity", 0.4)
    .selectAll("path")
    .data(chords)
    .join("path")
    .style("mix-blend-mode", "multiply")
    .attr("fill", (d) => color(names[d.source.index]))
    .attr("d", ribbon)

    // ----------------- Tooltip para los links -----------------
    .append("title")
    .text((d) => {
      const information = formatInformation(d, names);

      informations.push(information);

      return information;
    });

  // ----------------- Leyenda -----------------
  let totalValues = [];

  data.names.forEach((name, index) => {
    const values = data[index];

    totalValues.push(d3.sum(values));
  });

  group
    .select("text")
    .attr("font-weight", "bold")
    .text((d) => names[d.index] + " " + formatValue(totalValues[d.index]));

  // ----------------- Leyenda V2 -----------------
  // const legend = svg
  //   .append("g")
  //   .attr("transform", `translate(${width / 2 - 25}, ${height / 2 - 100})`)
  //   .attr("text-anchor", "end")
  //   .selectAll("g")
  //   .data(data)
  //   .join("g")
  //   .attr("transform", (d, i) => `translate(0, ${i * 25})`);

  // legend
  //   .append("rect")
  //   .attr("x", 0)
  //   .attr("y", 0)
  //   .attr("width", 19)
  //   .attr("height", 19)
  //   .attr("fill", (d, i) => color(i));

  // legend
  //   .append("text")
  //   .attr("x", -10)
  //   .attr("y", 10)
  //   .attr("dy", "0.3em")
  //   .text((d, i) => {
  //     return informations[i];
  //   });
}

function formatInformation(d, names) {
  return `${formatValue(d.source.value)} ${names[d.target.index]} → ${
    names[d.source.index]
  }${
    d.source.index === d.target.index
      ? ""
      : `\n${formatValue(d.target.value)} ${names[d.source.index]} → ${
        names[d.target.index]
      }`
  }`;
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  graphContainer,
  loadGraph,
});
// #endregion
</script>

<style lang="scss" scoped>
.svg-container {
  position: relative;
  width: 100%;
  height: 100%;

  :deep(svg) {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
